import React, { memo } from 'react'
import { Col, Input, Modal, Row, Space, Switch, Typography, Card, Divider, Button, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClipboardList, faSave, faTimes, faToggleOn, faEdit } from '@fortawesome/free-solid-svg-icons'
import BillingTool from '../../../Bookings/components/Examination Components/BillingTool'
import useBillingMacroForm from '../hooks/useBillingMacroForm'
import PropTypes from 'prop-types'
import { hexToRGBA } from '../../../lib/utils'

const { Text, Title } = Typography

const BillingMacroForm = memo(({ visible, setVisible, isNew, macro, setMacro, macros, setMacros, index }) => {
  const { token } = theme.useToken()
  const { macroName, handleOk, handleCancel, handleFieldChange, handleItemSelection } = useBillingMacroForm({
    isNew,
    macro,
    setMacro,
    macros,
    setMacros,
    index,
    setVisible
  })

  return (
    <Modal
      title={null}
      destroyOnClose
      open={visible}
      closable={false}
      onOk={handleOk}
      onCancel={handleCancel}
      width='80%'
      maskClosable={false}
      centered
      styles={{
        content: {
          borderRadius: '16px',
          padding: '0',
          overflow: 'hidden',
          boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
        }
      }}
      footer={null}
    >
      {/* Custom Header */}
      <div
        style={{
          background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${hexToRGBA(token.colorPrimary, 0.8)} 100%)`,
          padding: '32px',
          color: 'white'
        }}
      >
        <Row align='middle' justify='space-between'>
          <Col>
            <Space size={16}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faClipboardList} style={{ fontSize: '24px', color: 'white' }} />
              </div>
              <div>
                <Title level={3} style={{ margin: 0, color: 'white', fontWeight: '600' }}>
                  {isNew ? 'Create New Billing Macro' : `Edit Macro: ${macroName}`}
                </Title>
                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                  {isNew
                    ? 'Build a comprehensive billing package with procedures and materials'
                    : 'Modify the billing package configuration and items'}
                </Text>
              </div>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Modal Content */}
      <div style={{ padding: '32px' }}>
        {/* Configuration Section */}
        <Card
          style={{
            borderRadius: '12px',
            marginBottom: '32px',
            border: `1px solid ${hexToRGBA(token.colorBorder, 0.2)}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <div style={{ marginBottom: '20px' }}>
            <Title level={5} style={{ margin: '0 0 16px 0', color: token.colorTextHeading }}>
              <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px', color: token.colorPrimary }} />
              Macro Configuration
            </Title>
          </div>

          <Row gutter={[32, 24]}>
            <Col xs={24} sm={12} lg={8}>
              <Space direction='vertical' style={{ width: '100%' }} size={8}>
                <Text strong style={{ fontSize: '15px', color: token.colorTextHeading }}>
                  Status
                </Text>
                <div
                  style={{
                    padding: '16px',
                    borderRadius: '8px',
                    backgroundColor: hexToRGBA(macro.isActive ? token.colorSuccess : token.colorTextSecondary, 0.06),
                    border: `1px solid ${hexToRGBA(
                      macro.isActive ? token.colorSuccess : token.colorTextSecondary,
                      0.2
                    )}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Space>
                    <FontAwesomeIcon
                      icon={faToggleOn}
                      style={{
                        color: macro.isActive ? token.colorSuccess : token.colorTextSecondary,
                        fontSize: '16px'
                      }}
                    />
                    <Text style={{ fontWeight: '500' }}>{macro.isActive ? 'Active' : 'Inactive'}</Text>
                  </Space>
                  <Switch
                    checked={macro.isActive}
                    onChange={(checked) => handleFieldChange('isActive', checked)}
                    checkedChildren='Active'
                    unCheckedChildren='Inactive'
                    style={{
                      backgroundColor: macro.isActive ? token.colorSuccess : undefined
                    }}
                  />
                </div>
              </Space>
            </Col>

            <Col xs={24} sm={12} lg={16}>
              <Space direction='vertical' style={{ width: '100%' }} size={8}>
                <Text strong style={{ fontSize: '15px', color: token.colorTextHeading }}>
                  Macro Name
                </Text>
                <Input
                  value={macro.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder='Enter a descriptive name for this billing macro'
                  maxLength={50}
                  allowClear
                  size='large'
                  style={{
                    borderRadius: '8px',
                    fontSize: '16px',
                    padding: '12px 16px'
                  }}
                />
                <Text type='secondary' style={{ fontSize: '13px' }}>
                  Choose a clear name that describes the complete service package (e.g., "ECG Package", "Suture Kit")
                </Text>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Billing Items Section */}
        <Card
          style={{
            borderRadius: '12px',
            marginBottom: '32px',
            border: `1px solid ${hexToRGBA(token.colorBorder, 0.2)}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: '0' }}
        >
          <div style={{ padding: '24px 24px 0 24px' }}>
            <Title level={5} style={{ margin: '0 0 16px 0', color: token.colorTextHeading }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '8px', color: token.colorSuccess }} />
              Billing Items & Components
            </Title>
            <Text type='secondary' style={{ fontSize: '14px', marginBottom: '20px', display: 'block' }}>
              Add all procedures, materials, and medicines included in this macro package
            </Text>
          </div>

          <div style={{ background: hexToRGBA(token.colorBgLayout, 0.3), padding: '20px' }}>
            <BillingTool
              billing={{}}
              itemSelection={macro.items}
              setItemSelection={handleItemSelection}
              readOnly={false}
              isMacroForm={true}
            />
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button
            size='large'
            onClick={handleCancel}
            style={{
              borderRadius: '8px',
              height: '48px',
              padding: '0 24px',
              fontWeight: '500',
              fontSize: '15px'
            }}
          >
            <FontAwesomeIcon icon={faTimes} style={{ marginRight: '8px' }} />
            Cancel
          </Button>
          <Button
            type='primary'
            size='large'
            onClick={handleOk}
            style={{
              borderRadius: '8px',
              height: '48px',
              padding: '0 24px',
              fontWeight: '500',
              fontSize: '15px',
              boxShadow: `0 4px 12px ${hexToRGBA(token.colorPrimary, 0.3)}`
            }}
          >
            <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} />
            {isNew ? 'Create Macro' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </Modal>
  )
})

BillingMacroForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  isNew: PropTypes.bool.isRequired,
  macro: PropTypes.shape({
    isActive: PropTypes.bool,
    name: PropTypes.string,
    items: PropTypes.array
  }).isRequired,
  setMacro: PropTypes.func.isRequired,
  macros: PropTypes.array.isRequired,
  setMacros: PropTypes.func.isRequired,
  index: PropTypes.number
}

BillingMacroForm.displayName = 'BillingMacroForm'

export default BillingMacroForm
